// ── BeeConnect — Contextual connections (friendship / professional) ────────
// Grid layout — contextual filters (near me, same field, shared interests)
// No swipe, no romantic score, no compatibility radar
// Intent: meetups, collaboration, local community

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTr } from '../utils/useTr';
import { useApp } from '../contexts/AppContext';
import { fetchConnectionRequests, sendConnectionRequest, acceptConnectionRequest, declineConnectionRequest, getRequestStatus } from '../utils/connections';
import PeopleFilterPills from '../components/PeopleFilterPills';

// ── Design tokens ─────────────────────────────────────────────────────────
const G = {
  bg:'var(--black)', card:'var(--dark)', border:'var(--border)',
  white:'var(--white)', muted:'var(--muted)',
  teal:'#1D9E75', tealPale:'rgba(29,158,117,0.1)', tealBdr:'rgba(29,158,117,0.35)',
  gold:'var(--gold)', sans:"'Outfit',sans-serif", serif:"'Cormorant Garamond',serif",
};

// ── SVG icons for context filters ─────────────────────────────────────────
const FilterIcon = {
  near: () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  field: () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
    </svg>
  ),
  interests: () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  ),
  new: () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
};

// ── Context filter definitions ─────────────────────────────────────────────
function getContextFilters(tr) {
  return [
    { id:'near',     label: tr('beeconnect.filter_near')    || 'Near me',         Icon: FilterIcon.near      },
    { id:'field',    label: tr('beeconnect.filter_field')   || 'Same field',      Icon: FilterIcon.field     },
    { id:'interests',label: tr('beeconnect.filter_shared')  || 'Shared interests',Icon: FilterIcon.interests },
    { id:'new',      label: tr('beeconnect.filter_new')     || 'Recently joined', Icon: FilterIcon.new       },
  ];
}

// ── Shared interests count ─────────────────────────────────────────────────
function sharedCount(a, b) {
  const set = new Set(a?.interests || []);
  return (b?.interests || []).filter(i => set.has(i)).length;
}
function sharedList(a, b) {
  const set = new Set(a?.interests || []);
  return (b?.interests || []).filter(i => set.has(i));
}

// ── Same field heuristic (simple keyword match) ───────────────────────────
function sameField(a, b) {
  if (!a?.job || !b?.job) return false;
  const fields = ['design','tech','marketing','engineering','product','art','music',
                  'photo','food','health','finance','law','education','science'];
  const aJob = a.job.toLowerCase();
  const bJob = b.job.toLowerCase();
  return fields.some(f => aJob.includes(f) && bJob.includes(f));
}

// ── Interest chip ──────────────────────────────────────────────────────────
function Chip({ label, highlight }) {
  return (
    <span style={{
      display:'inline-block', fontSize:'0.63rem', padding:'0.2rem 0.6rem',
      background: highlight ? G.tealPale : 'rgba(255,255,255,0.05)',
      border: `1px solid ${highlight ? G.tealBdr : 'rgba(255,255,255,0.12)'}`,
      borderRadius:99, color: highlight ? G.teal : 'rgba(255,255,255,0.65)',
      fontFamily:G.sans, whiteSpace:'nowrap',
    }}>
      {label}
    </span>
  );
}

function chipLabel(interest) {
  const raw = interest.replace('interest.', '').replace(/_/g, ' ');
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

// ── Profile card — horizontal list row ────────────────────────────────────
function ConnectCard({ user, currentUser, connected, requestStatus, onConnect, onOpen, contextFilter, loading, pendingSent }) {
  const shared   = sharedList(currentUser, user);
  const sharedN  = shared.length;
  const distance = user.distance_km != null
    ? user.distance_km < 1 ? '<1 km' : `${Math.round(user.distance_km)} km`
    : null;

  // Badge text (top-right corner)
  const badge = contextFilter === 'near' && distance
    ? distance
    : sharedN > 0
    ? `${sharedN} interest${sharedN > 1 ? 's' : ''}`
    : null;

  // Up to 3 chips: shared first, then extras
  const extraInterests = (user.interests || []).filter(i => !shared.includes(i));
  const chips = [
    ...shared.slice(0, 2).map(i => ({ label: chipLabel(i), highlight: true })),
    ...extraInterests.slice(0, Math.max(0, 3 - Math.min(shared.length, 2))).map(i => ({ label: chipLabel(i), highlight: false })),
  ].slice(0, 3);

  const jobCity = [user.job, user.city].filter(Boolean).join(' · ');

  return (
    <div
      onClick={() => onOpen(user)}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: `1px solid ${connected ? G.tealBdr : 'rgba(255,255,255,0.09)'}`,
        borderRadius: 14,
        overflow: 'hidden',
        cursor: 'pointer',
        display: 'flex',
        gap: 0,
        transition: 'border-color 0.18s, box-shadow 0.18s',
      }}>

      {/* Left — square photo */}
      <div style={{ width: 100, minWidth: 100, height: 'auto', alignSelf: 'stretch', background: 'var(--charcoal)', position: 'relative', overflow: 'hidden' }}>
        {user.photos?.[0]
          ? <img src={user.photos[0]} alt={user.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}/>
          : <div style={{ width: '100%', height: '100%', minHeight: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily: G.serif, fontSize: '2.2rem', color: G.teal, opacity: 0.3 }}>
                {(user.name || '?')[0].toUpperCase()}
              </span>
            </div>}
      </div>

      {/* Right — content */}
      <div style={{ flex: 1, padding: '0.75rem 0.85rem 0.7rem', display: 'flex', flexDirection: 'column', gap: '0.3rem', minWidth: 0 }}>

        {/* Row 1: name + badge */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.4rem' }}>
          <p style={{ fontFamily: G.serif, fontSize: '1rem', fontWeight: 600, color: G.white, margin: 0, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {user.name}{user.age ? `, ${user.age}` : ''}
          </p>
          {badge && (
            <span style={{ fontSize: '0.55rem', padding: '0.15rem 0.55rem', background: G.tealPale, border: `1px solid ${G.tealBdr}`, borderRadius: 99, color: G.teal, fontFamily: G.sans, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {badge}
            </span>
          )}
        </div>

        {/* Row 2: job · city */}
        {jobCity && (
          <p style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.45)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {jobCity}
          </p>
        )}

        {/* Row 3: chips */}
        {chips.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.1rem' }}>
            {chips.map((c, i) => <Chip key={i} label={c.label} highlight={c.highlight}/>)}
          </div>
        )}

        {/* Row 4: bio + Connect button */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '0.6rem', marginTop: 'auto', paddingTop: '0.35rem' }}>
          {user.bio ? (
            <p style={{ fontSize: '0.68rem', fontStyle: 'italic', color: G.teal, margin: 0, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', flex: 1, minWidth: 0 }}>
              {user.bio}
            </p>
          ) : <span/>}
          {(() => {
            const isPending = (requestStatus?.status === 'pending' && requestStatus?.isSender) || pendingSent?.has(user.id);
            return (
              <button
                onClick={e => { e.stopPropagation(); if (!connected && !isPending) onConnect(user); }}
                disabled={connected || isPending || loading}
                style={{
                  flexShrink: 0,
                  padding: '0.38rem 0.85rem',
                  background: connected ? G.tealPale : isPending ? 'rgba(255,255,255,0.04)' : G.teal,
                  border: `1px solid ${connected ? G.teal : isPending ? G.border : G.teal}`,
                  borderRadius: 99,
                  color: connected ? G.teal : isPending ? G.muted : '#fff',
                  fontFamily: G.sans,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  cursor: connected || isPending ? 'default' : 'pointer',
                  transition: 'all 0.15s',
                  letterSpacing: '0.02em',
                  opacity: loading ? 0.6 : 1,
                }}>
                {connected ? '✓' : isPending ? '…' : 'Connect'}
              </button>
            );
          })()}
        </div>

      </div>
    </div>
  );
}

// ── Profile sheet (expanded view) ─────────────────────────────────────────
function ProfileSheet({ user, currentUser, connected, requestStatus, onConnect, onClose, onMessage, mutualConnections, lang }) {
  const tr = useTr();
  const shared = sharedList(currentUser, user);
  const distance = user.distance_km != null
    ? user.distance_km < 1 ? 'less than 1 km away' : `${Math.round(user.distance_km)} km away`
    : null;
  const [photoIdx, setPhotoIdx] = useState(0);
  const photos = (user.photos || []).filter(Boolean);

  return (
    <div onClick={onClose}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:200, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:'var(--dark)', borderRadius:'16px 16px 0 0', maxWidth:480, width:'100%', maxHeight:'85vh', overflowY:'auto' }}>
        {/* Photo gallery */}
        <div style={{ height:220, position:'relative', overflow:'hidden', flexShrink:0 }}>
          {photos.length > 0
            ? <img src={photos[photoIdx]} alt={user.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            : <div style={{ width:'100%', height:'100%', background:'var(--charcoal)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ fontFamily:G.serif, fontSize:'3rem', color:G.teal, opacity:0.25 }}>{(user.name||'?')[0]}</span>
              </div>}
          {photos.length > 1 && <>
            <div onClick={() => setPhotoIdx(i => (i - 1 + photos.length) % photos.length)}
              style={{ position:'absolute', left:0, top:0, width:'35%', height:'100%', cursor:'pointer' }}/>
            <div onClick={() => setPhotoIdx(i => (i + 1) % photos.length)}
              style={{ position:'absolute', right:0, top:0, width:'35%', height:'100%', cursor:'pointer' }}/>
            <div style={{ position:'absolute', top:8, left:8, right:8, display:'flex', gap:3 }}>
              {photos.map((_,i) => (
                <div key={i} onClick={() => setPhotoIdx(i)}
                  style={{ flex:1, height:2, background: i===photoIdx ? '#fff' : 'rgba(255,255,255,0.3)', borderRadius:1, cursor:'pointer' }}/>
              ))}
            </div>
            <div style={{ position:'absolute', bottom:8, right:8, background:'rgba(0,0,0,0.5)', borderRadius:99, padding:'2px 7px', fontSize:'0.6rem', color:'rgba(255,255,255,0.85)', fontFamily:G.sans }}>
              {photoIdx + 1}/{photos.length}
            </div>
          </>}
          <button onClick={onClose} style={{ position:'absolute', top:10, right:10, background:'rgba(0,0,0,0.5)', border:'none', color:'#fff', width:28, height:28, borderRadius:'50%', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'linear-gradient(transparent,rgba(0,0,0,0.75))', padding:'1rem 1rem 0.7rem' }}>
            <p style={{ fontSize:'1.05rem', fontWeight:600, color:'#fff', margin:0 }}>{user.name}</p>
            {user.job && <p style={{ fontSize:'0.72rem', color:G.teal, margin:'0.1rem 0 0' }}>{user.job}</p>}
            {distance && <p style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.55)', margin:'0.1rem 0 0' }}>{distance}</p>}
          </div>
        </div>
        <div style={{ padding:'1rem 1.2rem' }}>

        {/* Bio */}
        {user.bio && (
          <div style={{ background:'rgba(255,255,255,0.03)', border:`1px solid ${G.border}`, borderRadius:8, padding:'0.7rem 0.85rem', marginBottom:'0.9rem', marginTop:'0.2rem' }}>
            <p style={{ fontFamily:G.serif, fontStyle:'italic', fontSize:'0.88rem', color:G.white, opacity:0.8, lineHeight:1.6, margin:0 }}>
              "{user.bio}"
            </p>
          </div>
        )}

        {/* Shared interests */}
        {shared.length > 0 && (
          <div style={{ marginBottom:'0.9rem' }}>
            <p style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.12em', color:G.teal, textTransform:'uppercase', marginBottom:'0.4rem' }}>
              {shared.length} shared interest{shared.length > 1 ? 's' : ''}
            </p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'0.3rem' }}>
              {shared.map(i => <Chip key={i} label={chipLabel(i)} highlight/>)}
            </div>
          </div>
        )}

        {/* Other interests */}
        {user.interests?.filter(i => !shared.includes(i)).length > 0 && (
          <div style={{ marginBottom:'1rem' }}>
            <p style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.12em', color:G.muted, textTransform:'uppercase', marginBottom:'0.4rem' }}>
              Also into
            </p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'0.3rem' }}>
              {user.interests.filter(i => !shared.includes(i)).slice(0, 6).map(i =>
                <Chip key={i} label={chipLabel(i)}/>
              )}
            </div>
          </div>
        )}

        {/* Warm intro */}
        {mutualConnections?.length > 0 && !connected && (
          <div style={{ marginBottom:'0.9rem', padding:'0.7rem 0.85rem', background:'rgba(29,158,117,0.06)', border:`1px solid ${G.tealBdr}`, borderRadius:'var(--r-md)' }}>
            <p style={{ fontSize:'0.62rem', letterSpacing:'0.12em', textTransform:'uppercase', color:G.teal, marginBottom:'0.5rem' }}>
              {lang === 'fr' ? 'Connexions communes' : 'Common connections'}
            </p>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.5rem' }}>
              {mutualConnections.slice(0,2).map((m, i) => (
                <div key={m.id || i} style={{ display:'flex', alignItems:'center', gap:'0.35rem' }}>
                  <div style={{ width:24, height:24, borderRadius:'50%', overflow:'hidden', background:'var(--charcoal)', border:`1px solid ${G.tealBdr}` }}>
                    {m.photos?.[0]
                      ? <img src={m.photos[0]} alt={m.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                      : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.6rem', color:G.teal }}>{(m.name||'?')[0]}</div>}
                  </div>
                  <span style={{ fontSize:'0.72rem', color:'var(--fg)' }}>{m.name?.split(' ')[0]}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize:'0.68rem', color:G.muted, margin:'0 0 0.5rem', lineHeight:1.4 }}>
              {lang === 'fr'
                ? `${mutualConnections[0].name?.split(' ')[0]} pourrait vous présenter.`
                : `${mutualConnections[0].name?.split(' ')[0]} could introduce you.`}
            </p>
            <button
              onClick={() => onMessage?.(mutualConnections[0])}
              style={{ fontSize:'0.65rem', padding:'0.3rem 0.7rem', background:'transparent', border:`1px solid ${G.tealBdr}`, borderRadius:99, color:G.teal, cursor:'pointer', fontFamily:G.sans }}
            >
              {lang === 'fr' ? `Demander une intro à ${mutualConnections[0].name?.split(' ')[0]}` : `Ask ${mutualConnections[0].name?.split(' ')[0]} for an intro`}
            </button>
          </div>
        )}

        {/* Actions */}
        {(() => {
          const isPending  = requestStatus?.status === 'pending';
          const isSender   = requestStatus?.isSender;
          const isAccepted = connected;
          const firstName  = user.name?.split(' ')[0];
          return (
            <div style={{ display:'flex', gap:'0.6rem' }}>
              {isAccepted ? (
                <button disabled style={{ flex:2, padding:'0.8rem', background:G.tealPale, border:`1px solid ${G.teal}`, borderRadius:8, color:G.teal, fontFamily:G.sans, fontWeight:700, fontSize:'0.85rem', cursor:'default' }}>
                  ✓ Connecté·e
                </button>
              ) : isPending && isSender ? (
                <button disabled style={{ flex:2, padding:'0.8rem', background:'rgba(255,255,255,0.04)', border:`1px solid ${G.border}`, borderRadius:8, color:G.muted, fontFamily:G.sans, fontSize:'0.82rem', cursor:'default' }}>
                  Demande envoyée…
                </button>
              ) : (
                <button onClick={() => onConnect(user)} style={{ flex:2, padding:'0.8rem', background:G.teal, border:`1px solid ${G.teal}`, borderRadius:8, color:'#fff', fontFamily:G.sans, fontWeight:700, fontSize:'0.85rem', cursor:'pointer' }}>
                  Se connecter avec {firstName}
                </button>
              )}
              {isAccepted && (
                <button onClick={() => onMessage(user)}
                  style={{ flex:1, padding:'0.8rem', background:'transparent', border:`1px solid ${G.border}`, borderRadius:8, color:G.muted, fontFamily:G.sans, fontSize:'0.82rem', cursor:'pointer' }}>
                  Message
                </button>
              )}
            </div>
          );
        })()}
        </div>{/* end padding div */}
      </div>
    </div>
  );
}

// ── ModeSwitcher (used by Discover.js) ────────────────────────────────────
export function ModeSwitcher({ mode, onSwitch }) {
  const tr = useTr();
  return (
    <div style={{ display:'flex', background:'var(--charcoal)', borderRadius:99, padding:3, gap:2 }}>
      <button onClick={() => onSwitch('romantic')} style={{ flex:1, padding:'0.3rem 0.7rem', borderRadius:99, background: mode === 'romantic' ? 'var(--gold)' : 'transparent', border:'none', color: mode === 'romantic' ? 'var(--black)' : 'var(--muted)', fontSize:'0.7rem', fontWeight:600, cursor:'pointer', fontFamily:G.sans, whiteSpace:'nowrap', transition:'all 0.15s' }}>
        {tr('mode.romantic') || '♥ Romance'}
      </button>
      <button onClick={() => onSwitch('friendly')} style={{ flex:1, padding:'0.3rem 0.7rem', borderRadius:99, background: mode === 'friendly' ? G.teal : 'transparent', border:'none', color: mode === 'friendly' ? '#fff' : 'var(--muted)', fontSize:'0.7rem', fontWeight:600, cursor:'pointer', fontFamily:G.sans, whiteSpace:'nowrap', transition:'all 0.15s' }}>
        {tr('mode.friendly') || '✦ Connect'}
      </button>
    </div>
  );
}

// ── Main BeeConnect component ──────────────────────────────────────────────
export default function BeeConnect() {
  const tr = useTr();
  const {
    features, currentUser, users, usersInNetwork, matches, lang,
    setActiveChat, setPage, getCompatibility,
  } = useApp();
  // Local people filter — independent so a filter here doesn't empty Discover/BeeHive.
  const [connectFilter, setConnectFilter] = useState('all');

  const [contextFilter, setContextFilter] = useState('interests');
  const [openUser, setOpenUser]           = useState(null);
  const [tab, setTab]                     = useState('discover'); // 'discover' | 'connections' | 'pending'
  // Connection requests state
  const [requests, setRequests]           = useState({ sent: [], received: [] });
  const [reqLoading, setReqLoading]       = useState(false);
  // Optimistic: IDs we've just sent a request to (before DB confirms)
  const [pendingSent, setPendingSent]     = useState(new Set());
  const [connectError, setConnectError]   = useState('');

  // Load requests on mount
  const reloadRequests = useCallback(async () => {
    if (!currentUser?.id) return;
    const result = await fetchConnectionRequests(currentUser.id);
    setRequests(result);
  }, [currentUser?.id]);

  useEffect(() => { reloadRequests(); }, [reloadRequests]);

  // Derived: accepted connection IDs
  const acceptedIds = useMemo(() => {
    const allAccepted = [
      ...requests.sent.filter(r => r.status === 'accepted').map(r => r.to_user),
      ...requests.received.filter(r => r.status === 'accepted').map(r => r.from_user),
    ];
    return new Set(allAccepted);
  }, [requests]);

  // Pending received requests count (badge)
  const pendingReceived = requests.received.filter(r => r.status === 'pending');

  if (features?.beeconnect === false) {
    return (
      <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem', textAlign:'center' }}>
        <div>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'3rem', color:'var(--gold)', marginBottom:'1rem', lineHeight:1 }}>✦</div>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', fontWeight:400, color:'var(--white)', marginBottom:'0.5rem' }}>
            {tr('beeconnect.coming_soon')}
          </h2>
          <p style={{ fontSize:'0.82rem', color:'var(--muted)', lineHeight:1.7 }}>
            {tr('common.stay_tuned')}
          </p>
        </div>
      </div>
    );
  }

  const networkUsers = usersInNetwork || users || [];
  const CONTEXT_FILTERS = getContextFilters(tr);

  // Apply BeeConnect's own people filter (same vocabulary as Discover/BeeHive
  // but local state — no cross-page leakage).
  const peopleFiltered = useMemo(() => {
    const base = networkUsers.filter(u => u.id !== currentUser?.id);
    if (!connectFilter || connectFilter === 'all') return base;
    return base.filter(u => {
      if (connectFilter === 'verified')   return !!u.verified;
      if (connectFilter === 'nearby')     return u.distance_km != null ? u.distance_km <= 15 : true;
      if (connectFilter === 'compatible') { const c = getCompatibility?.(u.id); return c && c.score >= 70; }
      if (connectFilter === 'friends')    return u.mood === 'connecting' || u.seekingFriends || u.app_mode === 'friendly';
      return true;
    });
  }, [networkUsers, currentUser, connectFilter, getCompatibility]);

  // Apply context filter (BeeConnect-specific: near / field / interests / new)
  const filtered = useMemo(() => {
    const pool = peopleFiltered;
    switch (contextFilter) {
      case 'near':
        return pool
          .filter(u => u.distance_km != null && u.distance_km <= 5)
          .sort((a, b) => (a.distance_km||99) - (b.distance_km||99));
      case 'field':
        return pool.filter(u => sameField(currentUser, u));
      case 'interests':
        return pool
          .filter(u => sharedCount(currentUser, u) > 0)
          .sort((a, b) => sharedCount(currentUser, b) - sharedCount(currentUser, a));
      case 'new':
        return [...pool].sort((a, b) =>
          new Date(b.created_at||0) - new Date(a.created_at||0)
        ).slice(0, 20);
      default:
        return pool;
    }
  }, [peopleFiltered, contextFilter, currentUser]);

  const connectedUsers = networkUsers.filter(u => acceptedIds.has(u.id));

  const handleConnect = useCallback(async (user) => {
    if (!currentUser?.id) {
      setConnectError('You must be logged in to connect.');
      return;
    }
    const existing = getRequestStatus(requests, currentUser.id, user.id);
    if (existing?.status === 'accepted') return;
    if (existing?.status === 'pending' && existing?.isSender) return;
    if (pendingSent.has(user.id)) return;
    // Optimistic update — button changes immediately
    setPendingSent(prev => new Set([...prev, user.id]));
    setConnectError('');
    const { error } = await sendConnectionRequest(currentUser.id, user.id);
    if (error) {
      setPendingSent(prev => { const s = new Set(prev); s.delete(user.id); return s; });
      setConnectError(error.message || 'Connection request failed. Please try again.');
    } else {
      await reloadRequests();
      setPendingSent(prev => { const s = new Set(prev); s.delete(user.id); return s; });
    }
  }, [currentUser?.id, requests, reloadRequests, pendingSent]);

  const handleAccept = useCallback(async (requestId) => {
    setReqLoading(true);
    await acceptConnectionRequest(requestId, currentUser.id);
    await reloadRequests();
    setReqLoading(false);
  }, [currentUser?.id, reloadRequests]);

  const handleDecline = useCallback(async (requestId) => {
    setReqLoading(true);
    await declineConnectionRequest(requestId, currentUser.id);
    await reloadRequests();
    setReqLoading(false);
  }, [currentUser?.id, reloadRequests]);

  const handleMessage = async (user) => {
    // Only allow messaging accepted connections
    const status = getRequestStatus(requests, currentUser?.id, user.id);
    if (status?.status !== 'accepted') return;
    try {
      const { getOrCreateConversation } = await import('../utils/supabaseMessages');
      await getOrCreateConversation(currentUser?.id, user.id, 'friendly');
    } catch {}
    setActiveChat?.(user.id);
    setPage?.('messages');
  };

  return (
    <div style={{ minHeight:'100vh', background:G.bg, color:G.white, fontFamily:G.sans }}>

      {/* Header */}
      <div style={{ padding:'1rem 1.2rem 0', position:'sticky', top:0, background:G.bg, zIndex:10, borderBottom:`1px solid ${G.border}` }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.8rem' }}>
          <div>
            <h1 style={{ fontFamily:G.serif, fontSize:'1.5rem', fontWeight:400, margin:0 }}>
              <span style={{ color:G.teal }}>Bee</span>Connect
            </h1>
            <p style={{ fontSize:'0.68rem', color:G.muted, margin:'0.1rem 0 0' }}>
              Friendships · Collaborations · Community
            </p>
          </div>
          {/* Tab switcher */}
          <div style={{ display:'flex', background:'var(--charcoal)', borderRadius:99, padding:3, gap:2 }}>
            {[
              ['discover', 'Explore'],
              ['connections', connectedUsers.length ? `Connectés (${connectedUsers.length})` : 'Connectés'],
              ['pending', pendingReceived.length ? `Demandes (${pendingReceived.length})` : 'Demandes'],
            ].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} style={{ position:'relative', padding:'0.3rem 0.8rem', borderRadius:99, background:tab===id?G.teal:'transparent', border:'none', color:tab===id?'#fff':G.muted, fontSize:'0.63rem', fontWeight:600, cursor:'pointer', fontFamily:G.sans, whiteSpace:'nowrap', transition:'all 0.15s' }}>
                {label}
                {id === 'pending' && pendingReceived.length > 0 && (
                  <span style={{ position:'absolute', top:-2, right:-2, width:8, height:8, borderRadius:'50%', background:'#E2785A' }}/>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Same pills as Discover & BeeHive — local state */}
        {tab === 'discover' && (
          <div style={{ marginLeft:'-1.2rem', marginRight:'-1.2rem' }}>
            <PeopleFilterPills compact value={connectFilter} onChange={setConnectFilter}/>
          </div>
        )}

        {/* Context filter pills (BeeConnect-specific: near / field / interests / new) */}
        {tab === 'discover' && (
          <div style={{ display:'flex', gap:'0.4rem', overflowX:'auto', paddingBottom:'0.8rem', scrollbarWidth:'none' }}>
            {CONTEXT_FILTERS.map(f => (
              <button key={f.id} onClick={() => setContextFilter(f.id)} style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.3rem 0.8rem', borderRadius:99, background: contextFilter===f.id ? G.tealPale : 'transparent', border: `1px solid ${contextFilter===f.id ? G.teal : 'rgba(255,255,255,0.1)'}`, color: contextFilter===f.id ? G.teal : G.muted, fontSize:'0.7rem', fontWeight: contextFilter===f.id ? 600 : 400, cursor:'pointer', fontFamily:G.sans, whiteSpace:'nowrap', flexShrink:0, transition:'all 0.15s' }}>
                {f.Icon()} {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error banner */}
      {connectError && (
        <div style={{ margin:'0.6rem 1rem 0', padding:'0.55rem 0.85rem', background:'rgba(192,57,43,0.12)', border:'1px solid rgba(192,57,43,0.35)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.6rem' }}>
          <span style={{ fontSize:'0.72rem', color:'#E57373', fontFamily:G.sans }}>{connectError}</span>
          <span onClick={() => setConnectError('')} style={{ cursor:'pointer', color:'rgba(229,115,115,0.6)', fontSize:'0.9rem', lineHeight:1 }}>×</span>
        </div>
      )}

      <div style={{ padding:'1rem 1rem 5rem' }}>
        {tab === 'discover' ? (
          <>
            {filtered.length === 0 ? (
              <div style={{ textAlign:'center', padding:'3rem 1rem' }}>
                <div style={{ marginBottom:'0.8rem', display:'flex', justifyContent:'center' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={G.muted} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="8" y1="15" x2="8.01" y2="15"/><line x1="12" y1="15" x2="12.01" y2="15"/><line x1="16" y1="15" x2="16.01" y2="15"/><path d="M9 9h1a1 1 0 0 0 0-2H9a1 1 0 0 0 0 2z"/><path d="M14 9h1a1 1 0 0 0 0-2h-1a1 1 0 0 0 0 2z"/></svg>
                </div>
                <p style={{ fontFamily:G.serif, fontSize:'1.2rem', color:G.white, marginBottom:'0.3rem' }}>
                  {contextFilter === 'near' ? 'Nobody nearby right now'
                    : contextFilter === 'field' ? 'Nobody in your field yet'
                    : 'No shared interests found'}
                </p>
                <p style={{ fontSize:'0.78rem', color:G.muted, lineHeight:1.6 }}>
                  {contextFilter === 'near'
                    ? 'Make sure your location is set in your profile.'
                    : contextFilter === 'field'
                    ? 'Add your job title to connect with people in the same field.'
                    : 'Add more interests to your profile to find people like you.'}
                </p>
              </div>
            ) : (
              <>
                <p style={{ fontSize:'0.68rem', color:G.muted, marginBottom:'0.8rem', letterSpacing:'0.05em' }}>
                  {filtered.length} people · {CONTEXT_FILTERS.find(f => f.id === contextFilter)?.label}
                </p>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.65rem' }}>
                  {filtered.map(u => {
                    const reqStatus = getRequestStatus(requests, currentUser?.id, u.id);
                    return (
                      <ConnectCard
                        key={u.id}
                        user={u}
                        currentUser={currentUser}
                        connected={acceptedIds.has(u.id)}
                        requestStatus={reqStatus}
                        onConnect={handleConnect}
                        onOpen={setOpenUser}
                        contextFilter={contextFilter}
                        loading={reqLoading}
                        pendingSent={pendingSent}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </>
        ) : tab === 'connections' ? (
          /* Connections tab — accepted */
          connectedUsers.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem 1rem' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={G.muted} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom:'0.8rem' }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              <p style={{ fontFamily:G.serif, fontSize:'1.2rem', color:G.white, marginBottom:'0.3rem' }}>Aucune connexion</p>
              <p style={{ fontSize:'0.78rem', color:G.muted }}>Envoie des demandes dans l'onglet Explore.</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
              {connectedUsers.map(u => {
                const shared = sharedList(currentUser, u);
                return (
                  <div key={u.id} onClick={() => setOpenUser(u)}
                    style={{ background:G.card, border:`1px solid ${G.tealBdr}`, borderRadius:10, padding:'0.7rem 0.85rem', display:'flex', alignItems:'center', gap:'0.8rem', cursor:'pointer' }}>
                    <div style={{ width:40, height:40, borderRadius:8, overflow:'hidden', flexShrink:0, background:'var(--charcoal)' }}>
                      {u.photos?.[0]
                        ? <img src={u.photos[0]} alt={u.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                        : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ fontFamily:G.serif, fontSize:'1.1rem', color:G.teal, opacity:0.4 }}>{(u.name||'?')[0]}</span></div>}
                    </div>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:'0.85rem', fontWeight:600, color:G.white, margin:0 }}>{u.name}</p>
                      <p style={{ fontSize:'0.65rem', color:G.teal, margin:'0.1rem 0 0' }}>{u.job || (shared.length > 0 ? `${shared.length} intérêts communs` : 'Connecté·e')}</p>
                    </div>
                    <button onClick={e => { e.stopPropagation(); handleMessage(u); }}
                      style={{ padding:'0.3rem 0.75rem', background:G.tealPale, border:`1px solid ${G.teal}`, borderRadius:6, color:G.teal, fontSize:'0.7rem', fontWeight:600, cursor:'pointer', fontFamily:G.sans }}>
                      Message
                    </button>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* Pending tab — received + sent requests */
          (() => {
            const pendingSent = requests.sent.filter(r => r.status === 'pending');
            const hasAny = pendingReceived.length > 0 || pendingSent.length > 0;
            if (!hasAny) return (
              <div style={{ textAlign:'center', padding:'3rem 1rem' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={G.muted} strokeWidth="1.2" strokeLinecap="round" style={{ marginBottom:'0.8rem' }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <p style={{ fontFamily:G.serif, fontSize:'1.2rem', color:G.white, marginBottom:'0.3rem' }}>Aucune demande en attente</p>
                <p style={{ fontSize:'0.78rem', color:G.muted }}>Les demandes de connexion apparaîtront ici.</p>
              </div>
            );
            return (
              <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                {/* Received requests */}
                {pendingReceived.length > 0 && (
                  <div>
                    <p style={{ fontSize:'0.6rem', fontWeight:600, letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(226,120,90,0.85)', marginBottom:'0.55rem' }}>
                      Reçues — {pendingReceived.length}
                    </p>
                    <div style={{ display:'flex', flexDirection:'column', gap:'0.55rem' }}>
                      {pendingReceived.map(req => {
                        const sender = networkUsers.find(u => u.id === req.from_user);
                        if (!sender) return null;
                        return (
                          <div key={req.id} style={{ background:G.card, border:`1px solid rgba(226,120,90,0.25)`, borderRadius:10, padding:'0.85rem', display:'flex', alignItems:'center', gap:'0.8rem' }}>
                            <div onClick={() => setOpenUser(sender)} style={{ width:44, height:44, borderRadius:8, overflow:'hidden', flexShrink:0, background:'var(--charcoal)', cursor:'pointer' }}>
                              {sender.photos?.[0]
                                ? <img src={sender.photos[0]} alt={sender.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                                : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ fontFamily:G.serif, fontSize:'1.2rem', color:G.teal, opacity:0.4 }}>{(sender.name||'?')[0]}</span></div>}
                            </div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <p style={{ fontSize:'0.85rem', fontWeight:600, color:G.white, margin:0 }}>{sender.name}</p>
                              {req.message && <p style={{ fontSize:'0.68rem', color:G.muted, margin:'0.15rem 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>"{req.message}"</p>}
                              <p style={{ fontSize:'0.6rem', color:'rgba(226,120,90,0.8)', margin:'0.1rem 0 0' }}>Demande de connexion</p>
                            </div>
                            <div style={{ display:'flex', gap:'0.4rem', flexShrink:0 }}>
                              <button onClick={() => handleDecline(req.id)} disabled={reqLoading}
                                style={{ padding:'0.35rem 0.7rem', background:'transparent', border:`1px solid ${G.border}`, borderRadius:6, color:G.muted, fontSize:'0.7rem', cursor:'pointer', fontFamily:G.sans }}>
                                Refuser
                              </button>
                              <button onClick={() => handleAccept(req.id)} disabled={reqLoading}
                                style={{ padding:'0.35rem 0.7rem', background:G.teal, border:'none', borderRadius:6, color:'#fff', fontSize:'0.7rem', fontWeight:700, cursor:'pointer', fontFamily:G.sans }}>
                                Accepter
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Sent pending requests */}
                {pendingSent.length > 0 && (
                  <div>
                    <p style={{ fontSize:'0.6rem', fontWeight:600, letterSpacing:'0.14em', textTransform:'uppercase', color:G.muted, marginBottom:'0.55rem' }}>
                      Envoyées — {pendingSent.length}
                    </p>
                    <div style={{ display:'flex', flexDirection:'column', gap:'0.55rem' }}>
                      {pendingSent.map(req => {
                        const recipient = networkUsers.find(u => u.id === req.to_user);
                        if (!recipient) return null;
                        return (
                          <div key={req.id} onClick={() => setOpenUser(recipient)} style={{ background:G.card, border:`1px solid rgba(255,255,255,0.08)`, borderRadius:10, padding:'0.75rem', display:'flex', alignItems:'center', gap:'0.8rem', cursor:'pointer' }}>
                            <div style={{ width:40, height:40, borderRadius:8, overflow:'hidden', flexShrink:0, background:'var(--charcoal)' }}>
                              {recipient.photos?.[0]
                                ? <img src={recipient.photos[0]} alt={recipient.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                                : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ fontFamily:G.serif, fontSize:'1.1rem', color:G.teal, opacity:0.4 }}>{(recipient.name||'?')[0]}</span></div>}
                            </div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <p style={{ fontSize:'0.82rem', fontWeight:600, color:G.white, margin:0 }}>{recipient.name}</p>
                              <p style={{ fontSize:'0.6rem', color:G.muted, margin:'0.1rem 0 0' }}>En attente de réponse…</p>
                            </div>
                            <span style={{ fontSize:'0.58rem', padding:'0.2rem 0.6rem', background:'rgba(255,255,255,0.05)', border:`1px solid ${G.border}`, borderRadius:99, color:G.muted, fontFamily:G.sans, whiteSpace:'nowrap', flexShrink:0 }}>
                              Envoyée
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        )}
      </div>

      {/* Profile sheet */}
      {openUser && (() => {
        // Find mutual connections: accepted matches who share interests with openUser
        const targetInterests = new Set(openUser.interests || []);
        const mutuals = (matches || [])
          .filter(m => m.status === 'accepted' || !m.status)
          .filter(m => (m.interests || []).some(i => targetInterests.has(i)))
          .slice(0, 2);
        return (
          <ProfileSheet
            user={openUser}
            currentUser={currentUser}
            connected={acceptedIds.has(openUser.id)}
            requestStatus={getRequestStatus(requests, currentUser?.id, openUser.id)}
            onConnect={handleConnect}
            onClose={() => setOpenUser(null)}
            onMessage={handleMessage}
            mutualConnections={mutuals}
            lang={lang}
          />
        );
      })()}
    </div>
  );
}
